'use client'

import React, { useEffect, useState } from 'react'
import { OffersOffer, PaginationType } from '@/services/dataTypes'
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks'
import { apiOfferBanners } from '@/apis/offers'
import Banner from './Banner'
import { ChevronDown, ChevronUp, Loader2, Tag } from 'lucide-react'

interface Props {
    bannerResponse: OffersOffer[]
    domain: string
    mer_slug: string
    slug_type: string
    merchantId: string
    companyId: string
    pagination: PaginationType
}

const VerticalOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, merchantId, companyId, pagination }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([])
    const [page, setPage] = useState(2)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000))
        if (!pagination?.next_page) setHasMore(false)
    }, [bannerResponse])

    const loadMore = async () => {
        if (loading || !hasMore) return
        setLoading(true)
        try {
            const res = await apiOfferBanners(merchantId, companyId, page)
            const newBanners = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000)
            setBanners(prev => [...prev, ...newBanners])
            if (res?.data?.pagination?.next_page) { setPage(p => p + 1); setIsExpanded(true) }
            else setHasMore(false)
        } catch (e) { console.error(e) }
        finally { setLoading(false) }
    }

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000))
        setPage(2); setHasMore(true); setIsExpanded(false)
    }

    if (!banners.length) return null

    return (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #f0ede8' }}>
            <div className="flex items-center gap-2 px-5 py-4" style={{ background: '#8C5826' }}>
                <Tag size={14} style={{ color: '#F8F246' }} />
                <h4 className="text-xs font-black uppercase tracking-wider" style={{ color: '#ffffff' }}>Banner Offers</h4>
            </div>
            <div className="p-4 space-y-2">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data)
                    return (
                        <Banner key={i} data={offer_data} height={dimension?.height} width={dimension?.width}
                            mer_slug={mer_slug} slug_type={slug_type} domain={domain} />
                    )
                })}
            </div>
            <div className="flex gap-2 px-4 pb-4">
                {hasMore && (
                    <button onClick={loadMore} disabled={loading}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold rounded-xl transition-all disabled:opacity-50"
                        style={{ background: '#fffbf0', color: '#8C5826' }}>
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <ChevronDown size={12} />}
                        {loading ? 'Loading...' : 'Show More'}
                    </button>
                )}
                {isExpanded && (
                    <button onClick={showLess}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold rounded-xl transition-all"
                        style={{ background: 'rgba(139,88,38,0.06)', color: 'rgba(139,88,38,0.5)' }}>
                        <ChevronUp size={12} /> Show Less
                    </button>
                )}
            </div>
        </div>
    )
}

export default VerticalOfferBanner